/**
 * Client-side validation mirroring the EPS Public referral validators:
 *   PurchaseReferralValidator.cs and RefinanceReferralValidator.cs
 *
 * These checks are a fast-fail convenience for the user. The EPS API remains
 * the authoritative validator; we intentionally keep the messages and rules
 * aligned so behavior is predictable.
 */
import {
  PROPERTY_TYPES,
  PROPERTY_TYPE_LABELS,
  REFINANCE_TYPES,
  REFINANCE_TYPE_LABELS,
  type CreatePurchaseReferralRequest,
  type CreateRefinanceReferralRequest,
  type ReferralBorrowerRequest,
} from "../models/referral";

const PROPERTY_TYPE_LABEL_LIST = PROPERTY_TYPES.map((t) => PROPERTY_TYPE_LABELS[t]).join(", ");
const REFINANCE_TYPE_LABEL_LIST = REFINANCE_TYPES.map((t) => REFINANCE_TYPE_LABELS[t])
  .map((label) => `'${label}'`)
  .join(" or ");

const PHONE_REGEX = /^\d{10}$/;
const ZIP_REGEX = /^\d{5}$/;

export const VALID_STATE_CODES: ReadonlySet<string> = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "VI", "GU", "AS", "MP",
]);

const VALID_PROPERTY_TYPES: ReadonlySet<string> = new Set(PROPERTY_TYPES);
const VALID_REFINANCE_TYPES: ReadonlySet<string> = new Set(REFINANCE_TYPES);

export function isValidStateCode(stateCode: string | undefined): boolean {
  return (
    typeof stateCode === "string" &&
    stateCode.trim() !== "" &&
    VALID_STATE_CODES.has(stateCode.trim().toUpperCase())
  );
}

function isBlank(value: string | undefined): boolean {
  return value === undefined || value === null || value.trim() === "";
}

function validateBorrower(
  borrower: ReferralBorrowerRequest | undefined,
  prefix: string,
  errors: string[],
): void {
  if (!borrower) {
    errors.push(`${prefix} is required.`);
    return;
  }

  if (isBlank(borrower.firstName)) errors.push(`${prefix}.firstName is required.`);
  if (isBlank(borrower.lastName)) errors.push(`${prefix}.lastName is required.`);
  if (isBlank(borrower.email)) errors.push(`${prefix}.email is required.`);

  if (isBlank(borrower.phone)) {
    errors.push(`${prefix}.phone is required.`);
  } else if (!PHONE_REGEX.test(borrower.phone)) {
    errors.push(`${prefix}.phone must be exactly 10 digits.`);
  }

  if (!isBlank(borrower.state) && !isValidStateCode(borrower.state)) {
    errors.push(`${prefix}.state must be a valid 2-letter US state code.`);
  }
  if (!isBlank(borrower.zipCode) && !ZIP_REGEX.test(borrower.zipCode as string)) {
    errors.push(`${prefix}.zipCode must be a 5-digit zip code.`);
  }
}

function validatePropertyCommon(
  request: {
    propertyState: string;
    propertyZipCode?: string;
    propertyType: string;
    estimatedValue: number;
    loanAmount: number;
  },
  errors: string[],
): void {
  if (isBlank(request.propertyState)) {
    errors.push("propertyState is required.");
  } else if (!isValidStateCode(request.propertyState)) {
    errors.push("propertyState must be a valid 2-letter US state code.");
  }

  if (!isBlank(request.propertyZipCode) && !ZIP_REGEX.test(request.propertyZipCode as string)) {
    errors.push("propertyZipCode must be a 5-digit zip code.");
  }

  if (isBlank(request.propertyType)) {
    errors.push("propertyType is required.");
  } else if (!VALID_PROPERTY_TYPES.has(request.propertyType)) {
    errors.push(`propertyType must be one of: ${PROPERTY_TYPE_LABEL_LIST}.`);
  }

  if (!(request.estimatedValue > 0)) {
    errors.push("estimatedValue must be greater than 0.");
  }

  if (!(request.loanAmount > 0)) {
    errors.push("loanAmount must be greater than 0.");
  }
}

export function validatePurchase(request: CreatePurchaseReferralRequest | null): string[] {
  const errors: string[] = [];
  if (!request) {
    errors.push("Request body is required.");
    return errors;
  }

  validateBorrower(request.borrower, "borrower", errors);
  if (request.coBorrower) validateBorrower(request.coBorrower, "coBorrower", errors);
  validatePropertyCommon(request, errors);

  return errors;
}

/** A money amount is valid only when it is a finite number greater than zero. */
function isFinitePositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function validateRefinance(request: CreateRefinanceReferralRequest | null): string[] {
  const errors: string[] = [];
  if (!request) {
    errors.push("Request body is required.");
    return errors;
  }

  validateBorrower(request.borrower, "borrower", errors);
  if (request.coBorrower) validateBorrower(request.coBorrower, "coBorrower", errors);

  // propertyState / propertyZip / propertyType share the purchase rules.
  if (isBlank(request.propertyState)) {
    errors.push("propertyState is required.");
  } else if (!isValidStateCode(request.propertyState)) {
    errors.push("propertyState must be a valid 2-letter US state code.");
  }

  if (!isBlank(request.propertyZipCode) && !ZIP_REGEX.test(request.propertyZipCode as string)) {
    errors.push("propertyZipCode must be a 5-digit zip code.");
  }

  if (isBlank(request.propertyType)) {
    errors.push("propertyType is required.");
  } else if (!VALID_PROPERTY_TYPES.has(request.propertyType)) {
    errors.push(`propertyType must be one of: ${PROPERTY_TYPE_LABEL_LIST}.`);
  }

  // Refinance money fields must be finite as well as positive: Infinity and NaN
  // are never valid amounts.
  if (!isFinitePositive(request.currentLoanBalance)) {
    errors.push("currentLoanBalance must be greater than 0.");
  }

  if (!isFinitePositive(request.estimatedValue)) {
    errors.push("estimatedValue must be greater than 0.");
  }

  if (!isFinitePositive(request.loanAmount)) {
    errors.push("loanAmount must be greater than 0.");
  }

  if (isBlank(request.refinanceType)) {
    errors.push("refinanceType is required.");
  } else if (!VALID_REFINANCE_TYPES.has(request.refinanceType)) {
    errors.push(`refinanceType must be ${REFINANCE_TYPE_LABEL_LIST}.`);
  }

  return errors;
}
